import type { HTMLAttributes } from "react";

export interface UpdatedBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  /**
   * Optional date stamp shown after "updated ·" — typically an ISO date.
   * Omit it for a bare "updated" pill (the compact form, e.g. a sidebar nav
   * row where horizontal space is tight). Mirrors `NewBadge.date`.
   */
  date?: string;
}

/**
 * UpdatedBadge — the violet "updated" pill for a surface the reader ALREADY
 * had that has been replaced by a new version.
 *
 * Why a fourth pill rather than reusing one of the three that existed:
 *   - `NewBadge` (green) says "this did not exist before". Wrong for a
 *     replacement — the reader has used Baby Chops for weeks; only its
 *     install flow changed.
 *   - `NavStatusBadge` (amber "coming soon" / sky "nice to have") says "this
 *     is not built yet", and structurally cannot apply: `WebLeftMenu` gates
 *     it on `!released`, and an updated item is by definition released.
 *
 * The D&D HTML prototypes have carried an `update` pill for a while; the app
 * had no equivalent, which is why this primitive exists rather than a class.
 *
 * Palette: the `info` (violet #a78bfa) token family, in the same
 * `muted` fill + `strong` border + full-strength text arrangement
 * `NavStatusBadge` uses for its variants. Violet reads as "changed /
 * informational" against green="new" and amber="not yet", and is the only
 * remaining semantic token family not already spoken for by a state
 * (success / warning / error / conflict).
 *
 * Shape matches the other pills (`NewBadge` / `NavStatusBadge` /
 * `ContextBadge`): `rounded-full` (`--radius-full`), 10px/600 `text-micro`,
 * slight letter-spacing. Tokenized — no inline styles, no arbitrary
 * `text-[Npx]`.
 */
export function UpdatedBadge({ date, className, ...rest }: UpdatedBadgeProps) {
  const base =
    "inline-flex items-center gap-1 rounded-full bg-info-muted border border-info-strong px-2 py-0.5 text-micro tracking-[0.02em] whitespace-nowrap text-info";
  const classes = [base, className].filter(Boolean).join(" ");
  return (
    <span {...rest} className={classes}>
      {date ? `updated · ${date}` : "updated"}
    </span>
  );
}
